import { CREATE, DELETE, UPDATE } from '../../api';
import { isOk, serverErrorMessage } from '../../utils/errors';
import { TICKET_FORMAT_FILE, TICKET_UNIT_INDIVIDUAL } from './utils';

// CP-5799: ticket changes in the participant panels are staged like attachments -- picked files and removals sit
// in the form (`_ticketChanges`) until SAVE / Add user, then go to the backend's ticket routes
// (POST tour_user/<id>/tickets/, PUT|DELETE tour_user/<id>/tickets/<ticket>/, CP-5848):
//   { added: [{ key, file }], removed: [ticket id], replaced: { [ticket id]: file } }

export const NO_TICKET_CHANGES = { added: [], removed: [], replaced: {} };

export const hasTicketChanges = (changes) =>
    !!changes && (changes.added.length > 0 || changes.removed.length > 0 || Object.keys(changes.replaced).length > 0);

// What the guest sees the ticket as: the file's name without its extension ("Ticket.pdf" -> "Ticket").
export const ticketLabelFor = (fileName) => {
    const base = String(fileName || '').replace(/\\/g, '/').split('/').pop();
    const stem = base.includes('.') ? base.slice(0, base.lastIndexOf('.')) : base;
    return (stem.trim() || 'Ticket').slice(0, 256);
};

// Apply staged changes to booking `bookingId`, one request each: removals, replacements, then additions.
// Resolves to { tickets, errors }: the booking's tickets after what succeeded (the last list the backend sent,
// or `current` minus what was deleted) and the backend's words for each request it refused.
export const applyTicketChanges = async (dataAPI, bookingId, changes, current = []) => {
    const resource = `tour_user/${bookingId}/tickets`;
    let tickets = current;
    const errors = [];
    const removed = changes.removed || [];

    for (const id of removed) {
        const res = await dataAPI(DELETE, resource, { id });
        if (isOk(res)) {
            // DELETE resolves with no body (dataProvider): the list is ours minus the ticket.
            tickets = tickets.filter((t) => t.id !== id);
        } else {
            errors.push(serverErrorMessage(res, 'A ticket could not be deleted.'));
        }
    }
    for (const [id, file] of Object.entries(changes.replaced || {})) {
        if (removed.includes(+id)) {
            continue;
        }
        const res = await dataAPI(UPDATE, resource, { id: +id, data: { format: TICKET_FORMAT_FILE }, files: { file } });
        if (isOk(res) && Array.isArray(res.data?.tickets)) {
            tickets = res.data.tickets;
        } else {
            errors.push(`${file.name}: ${serverErrorMessage(res, 'the ticket could not be replaced.')}`);
        }
    }
    for (const { file } of changes.added || []) {
        const data = { label: ticketLabelFor(file.name), format: TICKET_FORMAT_FILE, unit: TICKET_UNIT_INDIVIDUAL, ticket_id: '' };
        const res = await dataAPI(CREATE, resource, { data, files: { file } });
        if (isOk(res) && Array.isArray(res.data?.tickets)) {
            tickets = res.data.tickets;
        } else {
            errors.push(`${file.name}: ${serverErrorMessage(res, 'the ticket could not be added.')}`);
        }
    }
    return { tickets, errors };
};
